import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { useToast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { authApi } from '@/lib/api/endpoints';
import type { SessionDevice } from '@/lib/api/types';

const SecurityPage = () => {
  const { notify } = useToast();
  const [sessions, setSessions] = useState<SessionDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const items = await authApi.listSessions();
      setSessions(items);
    } catch {
      notify('Не удалось загрузить сессии', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const handlePasswordChange = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword.length < 8) {
      notify('Новый пароль должен быть не короче 8 символов', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      notify('Подтверждение пароля не совпадает', 'error');
      return;
    }
    setChanging(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      notify('Пароль обновлён', 'success');
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Не удалось сменить пароль';
      notify(message, 'error');
    } finally {
      setChanging(false);
    }
  };

  const handleRevoke = async (deviceId: string, isCurrent: boolean) => {
    if (isCurrent) {
      notify('Текущую сессию нельзя отозвать здесь — выйдите из аккаунта', 'info');
      return;
    }
    try {
      await authApi.revokeSession(deviceId);
      notify('Сессия отозвана', 'success');
      await loadSessions();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Не удалось отозвать сессию';
      notify(message, 'error');
    }
  };

  return (
    <section className="role-cabinet">
      <div className="role-hero" style={{ '--role-accent': '#49ddb7' } as CSSProperties}>
        <div>
          <small>Центр безопасности</small>
          <h2>Центр безопасности</h2>
          <p>Смена пароля и управление доверенными устройствами текущей учётной записи.</p>
        </div>
      </div>

      <div className="role-grid" style={{ display: 'grid', gap: '1.5rem', marginTop: '1.5rem' }}>
        <form
          onSubmit={(e) => void handlePasswordChange(e)}
          className="role-panel"
          style={{ display: 'grid', gap: '0.75rem', maxWidth: 420 }}
        >
          <h3>Смена пароля</h3>
          <label>
            <span>Текущий пароль</span>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </label>
          <label>
            <span>Новый пароль</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>
          <label>
            <span>Подтверждение</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>
          <button type="submit" className="profile-btn primary" disabled={changing}>
            {changing ? 'Сохранение…' : 'Обновить пароль'}
          </button>
        </form>

        <div className="role-panel">
          <h3>Активные сессии</h3>
          {loading ? (
            <p>Загрузка…</p>
          ) : sessions.length === 0 ? (
            <p>Активных устройств пока нет.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.75rem' }}>
              {sessions.map((session) => (
                <li
                  key={session.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    paddingBottom: '0.75rem',
                  }}
                >
                  <div>
                    <strong>{session.device_label}</strong>
                    <div style={{ fontSize: '0.85rem', opacity: 0.75 }}>
                      {session.device_key}
                      {session.is_current ? ' · текущая сессия' : ''}
                      {session.last_seen_at
                        ? ` · ${new Date(session.last_seen_at).toLocaleString()}`
                        : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="profile-btn"
                    disabled={session.is_current}
                    onClick={() => void handleRevoke(session.id, session.is_current)}
                  >
                    Отозвать
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export default SecurityPage;
